import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from 'react-query';
import { postsAPI } from '../api/posts';
import GridItem from '../components/GridItem';
import './SearchPage.css';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hashtag = searchParams.get('hashtag');
  const [columns, setColumns] = useState(3);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(
    ['search', hashtag],
    ({ pageParam = 0 }) => postsAPI.searchByHashtag(hashtag, pageParam, 30),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.last) return undefined;
        return lastPage.number + 1;
      },
      enabled: !!hashtag,
    }
  );

  // 반응형 컬럼 수 조정
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setColumns(2);
      else if (width < 1024) setColumns(3);
      else setColumns(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer로 무한 스크롤
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    const sentinel = document.querySelector('.load-more-sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!hashtag) {
    return (
      <div className="search-container">
        <div className="search-empty">
          <p>검색할 해시태그를 선택해주세요.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="search-container">
        <div className="search-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← 뒤로
          </button>
          <h2>#{hashtag}</h2>
        </div>
        <div className="loading">검색 중...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="search-container">
        <div className="search-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← 뒤로
          </button>
          <h2>#{hashtag}</h2>
        </div>
        <div className="error-message">검색에 실패했습니다.</div>
      </div>
    );
  }

  const posts = data?.pages?.flatMap((page) => page.content) || [];
  const totalElements = data?.pages[0]?.totalElements || posts.length;

  // Post를 GridItem 형식으로 변환 (첫 번째 미디어 사용)
  const items = posts
    .filter(post => post.media && post.media.length > 0)
    .map(post => ({
      ...post.media[0],
      postId: post.postId,
    }));

  // Masonry 레이아웃: 아이템을 컬럼별로 분배
  const columnHeights = Array(columns).fill(0);
  const columnItems = Array.from({ length: columns }, () => []);

  items.forEach((item) => {
    // 가장 짧은 컬럼 찾기
    const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
    columnItems[shortestColumnIndex].push(item);
    // 높이 업데이트 (aspect ratio 기반)
    const aspectRatio = parseFloat(item.aspectRatio) || 1;
    columnHeights[shortestColumnIndex] += 1 / aspectRatio;
  });

  return (
    <div className="search-container">
      <div className="search-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← 뒤로
        </button>
        <h2>#{hashtag}</h2>
        <span className="result-count">{totalElements}개의 게시물</span>
      </div>

      {items.length === 0 ? (
        <div className="search-empty">
          <p>#{hashtag} 해시태그가 포함된 게시물이 없습니다.</p>
        </div>
      ) : (
        <>
          <div className="masonry-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {columnItems.map((column, colIndex) => (
              <div key={colIndex} className="masonry-column">
                {column.map((item) => (
                  <GridItem key={item.postId} item={item} />
                ))}
              </div>
            ))}
          </div>

          {hasNextPage && (
            <div className="load-more-sentinel">
              {isFetchingNextPage ? '더 불러오는 중...' : ''}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;

